import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMovementsData } from '@/common/integrations/menhir/employee/movements/movements.service';
import { useCancelReimbursementOrder } from '@/common/integrations/menhir/employee/orders/orders.hooks';
import { TOrderCategorySlug } from '@/common/integrations/menhir/employee/orders/orders.types';
import { IBenefitType } from '@/common/types/general';
import usePolling from '@/web/hooks/usePolling';
import { GenericModal, IconBan } from '@coverflex-tech/hypernova';
import css from './cancel-reimbursement-modal.module.css';

interface IProps {
  movementCategory?: IBenefitType | 'budgets';
  orderId: string;
  category: TOrderCategorySlug;
  onClose: () => void;
  onCancel: () => void;
}

export const CancelReimbursementModal = ({
  orderId,
  category,
  movementCategory,
  onClose,
  onCancel,
}: IProps) => {
  const { t } = useTranslation();
  const poll = usePolling(4);
  const { mutateAsync: cancelReimbursementOrder } =
    useCancelReimbursementOrder();
  const [submitting, setSubmitting] = useState(false);
  const waitForCancelledMovement = async () => {
    const movementsData = await getMovementsData({
      category: movementCategory,
    });

    if (
      movementsData?.movements?.list.find(
        (movement) => movement.order?.id === orderId
      )?.status === 'cancelled'
    ) {
      return;
    } else {
      throw new Error();
    }
  };

  const handleCancel = async () => {
    try {
      setSubmitting(true);
      await cancelReimbursementOrder({ category, orderId });
      if (movementCategory) {
        await poll(async () => await waitForCancelledMovement(), 1000);
      }
      onCancel();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GenericModal
      iconBadge={
        <div className={css.badge}>
          <IconBan />
        </div>
      }
      title={t('dashboard.cancel_reimbursement_modal.title')}
      content={t('dashboard.cancel_reimbursement_modal.description')}
      primaryAction={{
        children: t('dashboard.cancel_reimbursement_modal.button_label'),
        loading: submitting,
        onClick: handleCancel,
      }}
      onClose={onClose}
    />
  );
};
